import { useEffect, useMemo, useState } from "react";

const API_BASE = "https://laforgeataches.fr/go/php";

const pad = (n) => String(n).padStart(2, "0");

const toISO = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatDateFr = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });

const monthLabels = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getFrenchHolidays(year) {
  const easter = easterDate(year);

  return [
    ["Jour de l’an", `${year}-01-01`],
    ["Lundi de Pâques", toISO(addDays(easter, 1))],
    ["Fête du Travail", `${year}-05-01`],
    ["Victoire 1945", `${year}-05-08`],
    ["Ascension", toISO(addDays(easter, 39))],
    ["Lundi de Pentecôte", toISO(addDays(easter, 50))],
    ["Fête nationale", `${year}-07-14`],
    ["Assomption", `${year}-08-15`],
    ["Toussaint", `${year}-11-01`],
    ["Armistice", `${year}-11-11`],
    ["Noël", `${year}-12-25`],
  ];
}

export default function AnnualWorkHoursCalculator() {
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [weeklyHours, setWeeklyHours] = useState(35);
  const [workDays, setWorkDays] = useState([1, 2, 3, 4, 5]);
  const [entries, setEntries] = useState({});
  const [message, setMessage] = useState("");

  const hoursPerDay = workDays.length > 0 ? weeklyHours / workDays.length : 0;

  const holidaysMap = useMemo(() => {
    const map = {};
    getFrenchHolidays(Number(year)).forEach(([name, iso]) => {
      map[iso] = name;
    });
    return map;
  }, [year]);

  useEffect(() => {
    async function loadEntries() {
      setMessage("");

      try {
        const response = await fetch(`${API_BASE}/get-work-hours.php`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ year }),
        });

        const data = await response.json();

        if (data.success) {
          setEntries(data.entries || {});
        } else {
          setMessage(data.message || "Impossible de charger les heures.");
        }
      } catch (error) {
        setMessage("Erreur de chargement.");
      }
    }

    loadEntries();
  }, [year]);

  const rows = useMemo(() => {
    const list = [];
    const date = new Date(Number(year), 0, 1);

    while (date.getFullYear() === Number(year)) {
      const iso = toISO(date);
      const day = date.getDay();
      const month = date.getMonth() + 1;
      const isWorkDay = workDays.includes(day);
      const holidayName = holidaysMap[iso];
      const isNeutralHoliday = isWorkDay && Boolean(holidayName);

      if (isWorkDay) {
        const plannedHours = isNeutralHoliday ? 0 : hoursPerDay;
        const doneHours = Number(entries[iso] || 0);
        const diff = doneHours - plannedHours;

        list.push({
          iso,
          month,
          label: formatDateFr(iso),
          holidayName,
          plannedHours,
          doneHours,
          diff,
          isNeutralHoliday,
        });
      }

      date.setDate(date.getDate() + 1);
    }

    return list;
  }, [year, workDays, holidaysMap, hoursPerDay, entries]);

  const monthlyTotals = useMemo(() => {
    const months = monthLabels.map((label, index) => ({
      month: index + 1,
      label,
      planned: 0,
      done: 0,
      diff: 0,
      workDayCount: 0,
      holidayCount: 0,
    }));

    rows.forEach((row) => {
      const item = months[row.month - 1];
      item.planned += row.plannedHours;
      item.done += row.doneHours;
      item.diff = item.done - item.planned;
      item.workDayCount += 1;

      if (row.isNeutralHoliday) {
        item.holidayCount += 1;
      }
    });

    return months;
  }, [rows]);

  const selectedMonthRows = useMemo(
    () => rows.filter((row) => row.month === selectedMonth),
    [rows, selectedMonth]
  );

  const selectedMonthTotals = monthlyTotals[selectedMonth - 1];

  const totals = useMemo(() => {
    const planned = rows.reduce((sum, row) => sum + row.plannedHours, 0);
    const done = rows.reduce((sum, row) => sum + row.doneHours, 0);
    const diff = done - planned;

    return { planned, done, diff };
  }, [rows]);

  const updateEntry = (iso, value) => {
    setEntries((prev) => ({
      ...prev,
      [iso]: value,
    }));
  };

  const saveEntries = async () => {
    setMessage("Enregistrement...");

    try {
      const response = await fetch(`${API_BASE}/save-work-hours.php`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entries }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("Heures enregistrées.");
      } else {
        setMessage(data.message || "Erreur lors de l’enregistrement.");
      }
    } catch (error) {
      setMessage("Erreur réseau lors de l’enregistrement.");
    }
  };

  const fillPlannedHours = () => {
    const next = { ...entries };
    selectedMonthRows.forEach((row) => {
      next[row.iso] = row.plannedHours;
    });
    setEntries(next);
  };

  const resetSelectedMonthHours = () => {
    const next = { ...entries };
    selectedMonthRows.forEach((row) => {
      delete next[row.iso];
    });
    setEntries(next);
  };

  const toggleWorkDay = (day) => {
    setWorkDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort()
    );
  };

  const dayLabels = [
    ["Dim", 0],
    ["Lun", 1],
    ["Mar", 2],
    ["Mer", 3],
    ["Jeu", 4],
    ["Ven", 5],
    ["Sam", 6],
  ];

  return (
    <div className="annual-hours">
      <h2>Calcul des heures par mois</h2>

      {message && <div className="annual-message">{message}</div>}

      <div className="annual-hours-panel">
        <label>
          Année
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>

        <label>
          Mois affiché
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {monthLabels.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Heures par semaine
          <input
            type="number"
            step="0.25"
            value={weeklyHours}
            onChange={(e) => setWeeklyHours(Number(e.target.value))}
          />
        </label>

        <label>
          Heures par jour
          <input type="number" value={hoursPerDay.toFixed(2)} disabled />
        </label>
      </div>

      <div className="annual-days">
        {dayLabels.map(([label, value]) => (
          <button
            key={value}
            type="button"
            className={workDays.includes(value) ? "active" : ""}
            onClick={() => toggleWorkDay(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="annual-actions">
        <button type="button" onClick={saveEntries}>
          💾 Enregistrer
        </button>

        <button type="button" onClick={fillPlannedHours}>
          Remplir le mois affiché avec les heures prévues
        </button>

        <button type="button" onClick={resetSelectedMonthHours}>
          Remettre ce mois à zéro
        </button>
      </div>

      <div className="annual-summary">
        <div>
          <strong>{totals.planned.toFixed(2)} h</strong>
          <span>Total annuel prévu</span>
        </div>

        <div>
          <strong>{totals.done.toFixed(2)} h</strong>
          <span>Total annuel fait</span>
        </div>

        <div className={totals.diff >= 0 ? "positive" : "negative"}>
          <strong>{totals.diff.toFixed(2)} h</strong>
          <span>Solde annuel</span>
        </div>
      </div>

      <h3>Synthèse mensuelle</h3>

      <div className="annual-month-grid">
        {monthlyTotals.map((month) => (
          <button
            key={month.month}
            type="button"
            className={`annual-month-card ${
              selectedMonth === month.month ? "active" : ""
            } ${month.diff >= 0 ? "positive" : "negative"}`}
            onClick={() => setSelectedMonth(month.month)}
          >
            <strong>{month.label}</strong>
            <span>Prévu : {month.planned.toFixed(2)} h</span>
            <span>Fait : {month.done.toFixed(2)} h</span>
            <em>Solde : {month.diff.toFixed(2)} h</em>
          </button>
        ))}
      </div>

      <div className="annual-current-month-summary">
        <h3>{selectedMonthTotals.label} {year}</h3>
        <div>
          <span>Jours travaillés : {selectedMonthTotals.workDayCount}</span>
          <span>Jours fériés neutralisés : {selectedMonthTotals.holidayCount}</span>
          <span>Prévu : {selectedMonthTotals.planned.toFixed(2)} h</span>
          <span>Effectué : {selectedMonthTotals.done.toFixed(2)} h</span>
          <strong className={selectedMonthTotals.diff >= 0 ? "positive-text" : "negative-text"}>
            Solde : {selectedMonthTotals.diff.toFixed(2)} h
          </strong>
        </div>
      </div>

      <div className="annual-table-wrap">
        <table className="annual-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Prévu</th>
              <th>Fait</th>
              <th>Écart</th>
              <th>Info</th>
            </tr>
          </thead>

          <tbody>
            {selectedMonthRows.map((row) => (
              <tr
                key={row.iso}
                className={row.isNeutralHoliday ? "holiday-row" : ""}
              >
                <td>
                  <strong>{row.label}</strong>
                  <span>{row.iso}</span>
                </td>

                <td>{row.plannedHours.toFixed(2)} h</td>

                <td>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={entries[row.iso] ?? ""}
                    placeholder="0"
                    onChange={(e) => updateEntry(row.iso, e.target.value)}
                  />
                </td>

                <td
                  className={
                    row.diff > 0
                      ? "positive-text"
                      : row.diff < 0
                      ? "negative-text"
                      : ""
                  }
                >
                  {row.diff.toFixed(2)} h
                </td>

                <td>
                  {row.holidayName ? (
                    <span className="holiday-badge">{row.holidayName}</span>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
